<template>
  <div class="breadCrumb">
    <div><h3>Approvisionnement</h3></div>

    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Stock</li>
          <li class="breadcrumb-item">Approvisionnement</li>
          <li class="breadcrumb-item active">Création</li>
        </ol>
      </nav>
    </div>
  </div>

  <div class="card p-3">

    <!-- ================= HEADER ================= -->
    <div class="row mb-3">

      <div class="col-md-6">
        <label class="form-label">Date</label>
        <input type="date" class="form-control" v-model="form.date">
      </div>

      <div class="col-md-6">
        <label class="form-label">Statut</label>
        <select class="form-select" v-model="form.statut">
          <option value="EN_ATTENTE">EN_ATTENTE</option>
          <option value="VALIDE">VALIDE</option>
        </select>
      </div>

    </div>

    <hr>

    <!-- ================= LIGNES ================= -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5>Produits</h5>

      <button class="btn btn-success btn-sm" @click="addLigne">
        + Ajouter ligne
      </button>
    </div>

    <div
      v-for="(ligne, index) in form.ligneApprovs"
      :key="index"
      class="ligne-card mb-4"
    >

      <div class="row g-3 align-items-end">

        <!-- PRODUIT -->
        <div class="col-lg-4">
          <label class="form-label fw-semibold">Produit *</label>

          <Multiselect
            v-model="ligne.produit"
            :options="produits"
            valueProp="id"
            trackBy="nom"
            label="nom"
            placeholder="Choisir produit"
            :searchable="true"
            :canClear="true"
            @update:modelValue="onProduitChange(ligne)"
          >
            <template #option="{ option }">
              <div class="d-flex justify-content-between w-100">
                <div>
                  <strong>{{ option.nom }}</strong>
                  <div class="small text-muted">
                    Stock : {{ option.stock }}
                  </div>
                </div>

                <span class="badge" :class="stockClass(option.stock)">
                  {{ stockLabel(option.stock) }}
                </span>
              </div>
            </template>
          </Multiselect>
        </div>

        <!-- UNITE -->
        <div class="col-lg-2">
          <label class="form-label fw-semibold">Unité *</label>

          <select
            v-model="ligne.unite"
            class="form-select"
            @change="onUniteChange(ligne)"
          >
            <option disabled value="">-- unité --</option>

            <option
              v-if="getProduit(ligne.produit)"
              :value="getProduit(ligne.produit).uniteBase"
            >
              {{ getProduit(ligne.produit).uniteBase }} (base)
            </option>

            <option
              v-for="(val, key) in getProduit(ligne.produit)?.unites || {}"
              :key="key"
              :value="key"
            >
              {{ key }}
            </option>

          </select>
        </div>

        <!-- QUANTITE -->
        <div class="col-lg-1">
          <label class="form-label fw-semibold">Qté</label>

          <input
            type="number"
            min="1"
            v-model.number="ligne.quantite"
            class="form-control"
            @input="recalculLigne(ligne)"
          >
        </div>

        <!-- PRIX -->
        <div class="col-lg-2">
          <label class="form-label fw-semibold">Prix Achat</label>

          <input
            type="number"
            min="0"
            v-model.number="ligne.prixAchat"
            class="form-control"
            @input="recalculLigne(ligne)"
          >
        </div>

        <!-- EXPIRATION -->
        <div
          class="col-lg-2"
          v-if="isProduitAvecExpiration(ligne.produit)"
        >
          <label class="form-label fw-semibold">Expiration</label>

          <input
            type="date"
            v-model="ligne.dateExpiration"
            class="form-control"
            :min="today"
          >
        </div>

        <!-- TOTAL -->
        <div class="col-lg-1">
          <label class="form-label fw-semibold">Total</label>

          <input
            type="text"
            :value="ligneTotal(ligne)"
            class="form-control bg-light fw-bold"
            disabled
          >
        </div>

        <!-- DELETE -->
        <div class="col-lg-1">
          <button
            class="btn btn-danger w-100"
            @click="removeLigne(index)"
          >
            🗑
          </button>
        </div>

      </div>
    </div>

    <!-- ================= TOTAL ================= -->
    <div class="alert alert-success mt-3">
      <strong>Total :</strong> {{ totalGeneral.toLocaleString() }} FCFA
    </div>

    <!-- ================= ACTIONS ================= -->
    <div class="d-flex justify-content-between">
      <button class="btn btn-secondary" @click="cancel">
        Annuler
      </button>

      <button class="btn btn-primary" @click="submitForm" :disabled="loading">
        <span v-if="loading">Enregistrement...</span>
        <span v-else>Enregistrer</span>
      </button>
    </div>

  </div>
</template>

<script>
import api from '@/api'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'

export default {

  components: { Multiselect },

  data() {
    return {
      loading: false,
      produits: [],
      form: {
        date: '',
        statut: 'EN_ATTENTE',
        ligneApprovs: []
      }
    }
  },

  computed: {
    totalGeneral() {
      return this.form.ligneApprovs.reduce((sum, l) => {
        const p = this.getProduit(l.produit)
        if (!p) return sum

        const coef = p.unites?.[l.unite] || 1
        const qte = Number(l.quantite || 0) * coef

        return sum + (qte * Number(l.prixAchat || 0))
      }, 0)
    },

    today() {
      return new Date().toISOString().split('T')[0]
    }
  },

  methods: {

    addLigne() {
      this.form.ligneApprovs.push({
        produit: null,
        quantite: 1,
        prixAchat: 0,
        unite: '',
        dateExpiration: null
      })
    },

    removeLigne(i) {
      this.form.ligneApprovs.splice(i, 1)
    },

    async loadProduits() {
      const res = await api.getItems('produits')
      this.produits = res.data.data || res.data
    },

    getProduit(id) {
      return this.produits.find(p => Number(p.id) === Number(id))
    },

    onProduitChange(ligne) {
      const p = this.getProduit(ligne.produit)
      if (!p) return

      ligne.unite = p.uniteBase || ''
      ligne.prixAchat = Number(p.prixAchatMoyen || 0)

      this.recalculLigne(ligne)
    },

    onUniteChange(ligne) {
      this.recalculLigne(ligne)
    },

    recalculLigne(ligne) {
      const p = this.getProduit(ligne.produit)
      if (!p) return

      const coef = p.unites?.[ligne.unite] || 1

      ligne.total =
        Number(ligne.quantite || 0) *
        coef *
        Number(ligne.prixAchat || 0)
    },

    ligneTotal(ligne) {
      const p = this.getProduit(ligne.produit)
      if (!p) return '0'

      const coef = p.unites?.[ligne.unite] || 1

      return (
        Number(ligne.quantite || 0) *
        coef *
        Number(ligne.prixAchat || 0)
      ).toLocaleString() + ' FCFA'
    },

    isProduitAvecExpiration(id) {
      const p = this.getProduit(id)
      return p?.type?.toUpperCase() === 'MEDICAMENT'
    },

    stockClass(stock) {
      const s = Number(stock)
      if (s <= 0) return 'bg-danger'
      if (s <= 10) return 'bg-warning text-dark'
      return 'bg-success'
    },

    stockLabel(stock) {
      const s = Number(stock)
      if (s <= 0) return 'Rupture'
      if (s <= 10) return 'Faible'
      return 'Disponible'
    },

    validate() {
      if (!this.form.ligneApprovs.length) return false
      return this.form.ligneApprovs.every(l =>
        l.produit && l.quantite > 0 && l.prixAchat > 0
      )
    },

    async submitForm() {

      if (!this.validate()) return

      this.loading = true

      try {

        const payload = {
          date: this.form.date
            ? new Date(this.form.date).toISOString()
            : new Date().toISOString(),

          statut: this.form.statut,

          ligneApprovs: this.form.ligneApprovs.map(l => ({
            produit: Number(l.produit),
            quantite: Number(l.quantite),
            prixAchat: Number(l.prixAchat),
            unite: l.unite,
            dateExpiration: l.dateExpiration
              ? new Date(l.dateExpiration).toISOString()
              : null
          }))
        }

        await api.createItem('approvs', payload)

        alert("Approvisionnement enregistré ✅")
        this.$router.push({ name: 'approvs' })

      } finally {
        this.loading = false
      }
    },

    cancel() {
      this.$router.push({ name: 'approvs' })
    }
  },

  mounted() {
    this.loadProduits()
    this.addLigne()
  }
}
</script>

<style scoped>
.ligne-card {
  border: 1px solid #e5e7eb;
  border-radius: 15px;
  padding: 15px;
  background: #fafafa;
}
</style>